import { Subcription, SubscriptionRanking } from './namespace';
import { Scope } from './scopes';

export interface Feature {
  min_plan: Subcription;
  scopes: Scope[];
}

export const FEATURES: Record<string, Feature> = {
  // ─── STARTER (inclus dans tous les plans) ──────────────────
  ONLINE_BOOKING: {
    min_plan: Subcription.STARTER,
    scopes: ['agenda', 'catalog'],
  },
  CUSTOMER_BASE: {
    min_plan: Subcription.STARTER,
    scopes: ['customer'],
  },
  WEBSITE_AND_DOMAIN: {
    min_plan: Subcription.STARTER,
    scopes: ['website.builder', 'website.news', 'website.stats'],
  },
  GOOGLE_SYNC: {
    min_plan: Subcription.STARTER,
    scopes: ['establishment.google_sync'],
  },

  // ─── PRO ───────────────────────────────────────────────────
  // Marketing : la **lecture** des campagnes est libre (cf. FREE_SCOPES, pour qu'un
  // pro qui downgrade conserve l'accès à son historique). Seules la création et les
  // actions (envoi, planification, édition, suppression) sont gatées au plan PRO+.
  CAMPAIGN: {
    min_plan: Subcription.PRO,
    scopes: ['campaign.create', 'campaign.update', 'campaign.delete', 'campaign.send'],
  },
  BOOKING_WIDGET: {
    min_plan: Subcription.PRO,
    scopes: [],
  },
  // Paiement en ligne : la lecture (historique paiements via sales.payment.read, et le statut
  // du compte Stripe Connect + lien dashboard via stripe_connect.read) reste libre — un pro qui
  // downgrade doit pouvoir consulter ses paiements et atteindre son compte Stripe. Seules la
  // création/MAJ de paiements et la connexion/onboarding Stripe (stripe_connect.update) sont PRO.
  PAYMENT_ONLINE: {
    min_plan: Subcription.PRO,
    scopes: ['sales.payment.create', 'sales.payment.update', 'establishment.stripe_connect.update'],
  },
  // Bons / cartes cadeaux : consultation (`read`) et **encaissement d'un bon déjà vendu**
  // (`update` → useVoucher) restent libres (cf. FREE_SCOPES) — un client a pu payer sa carte
  // cadeau avant un downgrade, le pro doit pouvoir l'honorer. Seules la VENTE de nouveaux bons
  // (`create`) et l'annulation (`delete`) sont gatées au plan PRO.
  VOUCHER_GIFT_CARD: {
    min_plan: Subcription.PRO,
    scopes: ['sales.voucher.create', 'sales.voucher.delete'],
  },
  // Codes promo (B2C) : consultation, modification et suppression restent libres (cf. FREE_SCOPES)
  // pour que le pro garde la main sur ses codes après un downgrade (sans PRO, la modification se
  // réduit à la désactivation). Seule la création est gatée au plan PRO.
  PROMO_CODE: {
    min_plan: Subcription.PRO,
    scopes: ['sales.promo_code.create'],
  },
  EMAIL_PRO: {
    min_plan: Subcription.PRO,
    scopes: ['establishment.email_pro'],
  },
  // TEMPORAIRE : blocage des thèmes/palettes premium du builder désactivé — ouvert à tous les
  // plans pour le moment. Repasser min_plan à Subcription.PRO pour réactiver le gating.
  BUILDER_PREMIUM: {
    min_plan: Subcription.STARTER,
    scopes: ['website.premium'],
  },
  TRACKING: {
    min_plan: Subcription.PRO,
    scopes: ['website.tracking'],
  },
  // Statistiques « analyse » : historique 12 mois, comparaison de périodes, analyse par
  // prestation, export CSV, détail par fiche Google, attribution des campagnes. Domaine FRÈRE de
  // `stats` (libre dans FREE_SCOPES) : un enfant `stats.advanced` serait accordé à tous.
  ADVANCED_STATS: {
    min_plan: Subcription.PRO,
    scopes: ['stats_advanced'],
  },

  // ─── MAX ───────────────────────────────────────────────────
  PAYMENT_ADVANCED: {
    min_plan: Subcription.MAX,
    scopes: ['sales.payment.advanced'],
  },
  BOOKING_SMS_REMIND: {
    min_plan: Subcription.MAX,
    scopes: [],
  },
  API_ACCESS: {
    min_plan: Subcription.MAX,
    scopes: ['api', 'api.mcp'],
  },
  WEBHOOKS: {
    min_plan: Subcription.MAX,
    scopes: ['webhook'],
  },
  BOOST_SEO: {
    min_plan: Subcription.MAX,
    scopes: ['establishment.seo_content'],
  },
  // Édition de zone DNS self-service : lecture ET écriture gatées MAX (pas de split
  // FREE_SCOPES — le teaser est la carte verrouillée, la zone n'a pas de valeur
  // « historique » à préserver après un downgrade).
  DNS_ZONE_EDIT: {
    min_plan: Subcription.MAX,
    scopes: ['website.dns'],
  },
  // Scripts custom injectés sur le site (liste structurée, cf. ScriptEntrySchema).
  // L'écriture et la livraison publique sont gatées MAX ; la lecture des scripts
  // stockés reste accessible via GET /settings (philosophie historique-consultable).
  CUSTOM_SCRIPTS: {
    min_plan: Subcription.MAX,
    scopes: ['website.scripts'],
  },
  // ─── MAX ───────────────────────────────────────────────────
  // Articles SEO (moteur de contenu, 2 articles/semaine) : réservé à la formule la plus chère
  // (décision 2026-09-04). La lecture des publications reste `website.news` (STARTER).
  SEO_ARTICLES: {
    min_plan: Subcription.MAX,
    scopes: ['website.seo_articles'],
  },
  // Statistiques « pilotage » : détail par membre et par lieu, taux d'occupation, heures de
  // pointe. Domaine FRÈRE de `stats`, cf. ADVANCED_STATS.
  TEAM_STATS: {
    min_plan: Subcription.MAX,
    scopes: ['stats_team'],
  },
};

// Scopes accessibles quel que soit le plan
const FREE_SCOPES: Scope[] = [
  'establishment.info',
  // Réservé à l'OWNER par les rôles (aucun rôle ne le porte hormis `*`), jamais par le plan.
  'establishment.contact_email',
  'establishment.place',
  'establishment.team',
  'establishment.subscription',
  'establishment.resource',
  'establishment.tag',
  'sales.payment.read',
  // Historique marketing toujours consultable, même sans plan PRO (downgrade) :
  // couvre campaign.email.read + campaign.sms.read via le raccourci `domaine.verbe`.
  'campaign.read',
  // Bons / cartes cadeaux : consultation libre + encaissement d'un bon déjà vendu
  // (useVoucher = verbe `update`). La vente (`create`) et l'annulation (`delete`) restent
  // gatées PRO. Le rôle continue de gouverner (un VIEWER n'a pas `update`).
  'sales.voucher.read',
  'sales.voucher.update',
  // Codes promo (B2C) : consultation, modification et suppression libres — après un downgrade,
  // le pro doit pouvoir couper un code déjà distribué. Sans le plan PRO, la modification se
  // réduit à la désactivation (`ProPromoCodeService.updateProPromoCode`) ; seule la création
  // reste gatée PRO via la feature PROMO_CODE. Le rôle continue de gouverner.
  'sales.promo_code.read',
  'sales.promo_code.update',
  'sales.promo_code.delete',
  // Statut du compte Stripe Connect + lien vers le dashboard Stripe : consultable même
  // après un downgrade (l'argent du pro vit chez Stripe). La connexion/onboarding
  // (stripe_connect.update) reste gatée PRO via PAYMENT_ONLINE.
  'establishment.stripe_connect.read',
  'stats',
  'notification',
];

export function resolvePlanScopes(plan: Subcription): Scope[] {
  const planRank = SubscriptionRanking[plan];
  const scopes: Scope[] = [...FREE_SCOPES];
  for (const feature of Object.values(FEATURES)) {
    if (planRank >= SubscriptionRanking[feature.min_plan]) {
      scopes.push(...feature.scopes);
    }
  }
  return scopes;
}
